import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useEditor, EditorContent, Extension } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import * as Y from 'yjs';
import { WebsocketProvider } from 'y-websocket';
import { ySyncPlugin, yCursorPlugin, yUndoPlugin } from 'y-prosemirror';
import axios from 'axios';
import {
  ClipboardList, Plus, Pencil, Trash2, ChevronUp, ChevronDown,
  ChevronLeft, ChevronRight, Calendar, Search,
  Bold, Italic, Strikethrough, Heading1, Heading2,
  List, ListOrdered, ImagePlus, WifiOff, X, Baseline,
} from 'lucide-react';
import { PAPER_BASE } from '../config';
import { authSelector } from '../../../features/auth/slice';

const COLORS = ['#a78bfa','#f87171','#fb923c','#facc15','#4ade80','#22d3ee','#818cf8','#e879f9'];
function getUserColor(id) { return COLORS[id % COLORS.length]; }

async function uploadImage(file) {
  const fd = new FormData();
  fd.append('image', file);
  const r = await axios.post('/api/paper-task/upload', fd);
  return r.data.url;
}

const TEXT_COLORS = [
  { label: '기본', color: null },
  { label: '흰색', color: '#ffffff' },
  { label: '빨강', color: '#f87171' },
  { label: '주황', color: '#fb923c' },
  { label: '노랑', color: '#facc15' },
  { label: '초록', color: '#4ade80' },
  { label: '하늘', color: '#22d3ee' },
  { label: '보라', color: '#a78bfa' },
  { label: '핑크', color: '#f4b8b8' },
  { label: '회색', color: '#9ca3af' },
];

function Toolbar({ editor, onImageUpload }) {
  const [showColorPicker, setShowColorPicker] = useState(false);
  if (!editor) return null;
  const btn = (active, onClick, Icon, title) => (
    <button onClick={onClick} title={title}
      className={`p-1.5 rounded ${active ? 'bg-violet-400/20 text-violet-400' : 'text-white/40 hover:text-white hover:bg-white/5'}`}>
      <Icon size={15} />
    </button>
  );
  const currentColor = editor.getAttributes('textStyle').color || null;
  return (
    <div className="flex items-center gap-0.5 px-3 py-1.5 border-b border-white/5 bg-white/[0.02] flex-wrap">
      {btn(editor.isActive('bold'), () => editor.chain().focus().toggleBold().run(), Bold, '굵게')}
      {btn(editor.isActive('italic'), () => editor.chain().focus().toggleItalic().run(), Italic, '기울임')}
      {btn(editor.isActive('strike'), () => editor.chain().focus().toggleStrike().run(), Strikethrough, '취소선')}
      <div className="w-px h-4 bg-white/10 mx-0.5" />
      {btn(editor.isActive('heading', { level: 1 }), () => editor.chain().focus().toggleHeading({ level: 1 }).run(), Heading1, '제목 1')}
      {btn(editor.isActive('heading', { level: 2 }), () => editor.chain().focus().toggleHeading({ level: 2 }).run(), Heading2, '제목 2')}
      <div className="w-px h-4 bg-white/10 mx-0.5" />
      {btn(editor.isActive('bulletList'), () => editor.chain().focus().toggleBulletList().run(), List, '글머리 기호')}
      {btn(editor.isActive('orderedList'), () => editor.chain().focus().toggleOrderedList().run(), ListOrdered, '번호 목록')}
      <div className="w-px h-4 bg-white/10 mx-0.5" />
      {/* 글자색 */}
      <div className="relative">
        <button title="글자색" onClick={() => setShowColorPicker(v => !v)}
          className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5 flex flex-col items-center gap-0.5">
          <Baseline size={15} style={{ color: currentColor || undefined }} />
          <div className="w-3.5 h-1 rounded-sm" style={{ background: currentColor || 'rgba(255,255,255,0.2)' }} />
        </button>
        {showColorPicker && (
          <div className="absolute top-full left-0 mt-1 z-50 bg-[#1a1428] border border-white/10 rounded-lg p-2 flex gap-1.5 flex-wrap w-[136px]">
            {TEXT_COLORS.map(({ label, color }) => (
              <button key={label} title={label}
                onClick={() => { color ? editor.chain().focus().setColor(color).run() : editor.chain().focus().unsetColor().run(); setShowColorPicker(false); }}
                className={`w-5 h-5 rounded-full border-2 transition-transform hover:scale-110 ${currentColor === color ? 'border-white' : 'border-transparent'}`}
                style={{ background: color || 'linear-gradient(135deg, #fff 45%, #f00 45%)' }}
              />
            ))}
          </div>
        )}
      </div>
      <div className="w-px h-4 bg-white/10 mx-0.5" />
      <button onClick={onImageUpload} title="이미지" className="p-1.5 rounded text-white/40 hover:text-white hover:bg-white/5">
        <ImagePlus size={15} />
      </button>
    </div>
  );
}

function getLocalDate() {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const d = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${d}`;
}

function formatDate(dateStr) {
  const [, m, d] = dateStr.split('-');
  return `${parseInt(m)}월 ${parseInt(d)}일`;
}

function formatWeekday(dateStr) {
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('ko-KR', { weekday: 'long', timeZone: 'Asia/Seoul' });
}

// ─── 일일업무 섹션 ───
function DailyWorkSection({ me }) {
  const [date, setDate] = useState(getLocalDate());
  // 페이지 포커스 시 날짜 보정
  useEffect(() => {
    const onFocus = () => {
      const today = getLocalDate();
      setDate(prev => prev === getLocalDate() ? prev : today);
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);
  const [connected, setConnected] = useState(false);
  const [peers, setPeers] = useState([]);
  const peersRef = useRef('[]');
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const fileInputRef = useRef(null);
  const dateInputRef = useRef(null);
  const editorKey = useRef(0);

  const { ydoc, provider } = useMemo(() => {
    editorKey.current += 1;
    const doc = new Y.Doc();
    const token = localStorage.getItem('token');
    const apiUrl = process.env.REACT_APP_API_URL || 'https://api.urpink.co.kr';
    const wsUrl = apiUrl.replace('https://', 'wss://').replace('http://', 'ws://') + '/yjs';
    const prov = new WebsocketProvider(wsUrl, `paper-daily-${date}`, doc, { params: { token } });
    return { ydoc: doc, provider: prov };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  useEffect(() => {
    if (provider.wsconnected) setConnected(true);
    provider.on('status', ({ status }) => setConnected(status === 'connected'));
    provider.awareness.setLocalStateField('user', {
      name: me?.nickName || me?.name || 'Unknown',
      color: getUserColor(me?.id || 0),
      profileImage: me?.profileImage || null,
    });
    const awarenessHandler = () => {
      const seen = new Set();
      const users = [];
      const myName = me?.nickName || me?.name || 'Unknown';
      provider.awareness.getStates().forEach((state) => {
        if (state.user && !seen.has(state.user.name)) {
          seen.add(state.user.name);
          users.push({ name: state.user.name, color: state.user.color, profileImage: state.user.profileImage, isMe: state.user.name === myName });
        }
      });
      if (!seen.has(myName)) {
        users.unshift({ name: myName, color: getUserColor(me?.id || 0), profileImage: me?.profileImage || null, isMe: true });
      } else {
        const idx = users.findIndex(u => u.isMe);
        if (idx > 0) users.unshift(users.splice(idx, 1)[0]);
      }
      const pStr = JSON.stringify(users);
      if (pStr !== peersRef.current) { peersRef.current = pStr; setPeers(users); }
    };
    provider.awareness.on('change', awarenessHandler);
    awarenessHandler();
    return () => { provider.destroy(); ydoc.destroy(); };
  }, [provider, ydoc, me]);

  const YjsExtension = useMemo(() => Extension.create({
    name: 'yjs',
    addProseMirrorPlugins() {
      const fragment = ydoc.getXmlFragment('content');
      return [ySyncPlugin(fragment), yCursorPlugin(provider.awareness), yUndoPlugin()];
    },
  }), [ydoc, provider]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ history: false }),
      YjsExtension,
      Image.configure({ inline: false, allowBase64: false }),
      Placeholder.configure({ placeholder: '오늘의 업무를 입력하세요...' }),
      TextStyle,
      Color,
    ],
    editorProps: {
      attributes: { class: 'prose prose-invert prose-sm max-w-none focus:outline-none min-h-[200px] px-4 py-3' },
      handleDrop: (view, event) => {
        const files = event.dataTransfer?.files;
        if (files?.length) {
          event.preventDefault();
          Array.from(files).forEach(async (f) => {
            if (!f.type.startsWith('image/')) return;
            const url = await uploadImage(f);
            view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
          });
          return true;
        }
        return false;
      },
      handlePaste: (view, event) => {
        const items = event.clipboardData?.items;
        if (!items) return false;
        for (const item of items) {
          if (item.type.startsWith('image/')) {
            event.preventDefault();
            const file = item.getAsFile();
            if (!file) continue;
            (async () => {
              const url = await uploadImage(file);
              view.dispatch(view.state.tr.replaceSelectionWith(view.state.schema.nodes.image.create({ src: url })));
            })();
            return true;
          }
        }
        return false;
      },
    },
  }, [ydoc, editorKey.current]);

  const handleImageButton = () => fileInputRef.current?.click();
  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files?.length || !editor) return;
    for (const f of files) {
      if (!f.type.startsWith('image/')) continue;
      const url = await uploadImage(f);
      editor.chain().focus().setImage({ src: url }).run();
    }
    e.target.value = '';
  };

  const goDate = (offset) => {
    const d = new Date(date + 'T12:00:00');
    d.setDate(d.getDate() + offset);
    setDate(d.toLocaleDateString('en-CA', { timeZone: 'Asia/Seoul' }));
  };
  const goToday = () => setDate(getLocalDate());
  const isToday = date === getLocalDate();

  const doSearch = useCallback(async () => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const r = await axios.get('/api/paper-daily-work/search', { params: { q: searchQuery } });
      setSearchResults(r.data);
    } catch { setSearchResults([]); }
    setSearching(false);
  }, [searchQuery]);
  useEffect(() => { doSearch(); }, [doSearch]);

  const handleSearchSubmit = () => setSearchQuery(searchInput);
  const clearSearch = () => { setSearchInput(''); setSearchQuery(''); setSearchResults([]); };

  const getSnippet = (html, query) => {
    const text = html?.replace(/<[^>]*>/g, '') || '';
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text.slice(0, 80);
    const start = Math.max(0, idx - 25);
    const end = Math.min(text.length, idx + query.length + 40);
    return (start > 0 ? '...' : '') + text.slice(start, end) + (end < text.length ? '...' : '');
  };

  return (
    <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden mb-6">
      {/* 헤더: 날짜 가운데 + 우측 접속자/검색 */}
      <div className="relative flex items-center justify-center px-4 py-3 border-b border-white/5 bg-white/[0.02]">
        <div className="flex items-center gap-2">
          <button onClick={() => goDate(-1)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
            <ChevronLeft size={18} />
          </button>
          <button onClick={() => dateInputRef.current?.showPicker?.()} className="flex items-center gap-2 relative hover:bg-white/5 rounded-lg px-3 py-1.5 transition-colors">
            <Calendar size={16} className="text-violet-400" />
            <span className="text-white text-base font-bold">{formatDate(date)}</span>
            <span className="text-white/40 text-sm">{formatWeekday(date)}</span>
            <input ref={dateInputRef} type="date" value={date} onChange={e => e.target.value && setDate(e.target.value)}
              className="absolute inset-0 opacity-0 pointer-events-none" />
          </button>
          <button onClick={() => goDate(1)} className="p-1.5 text-white/30 hover:text-white rounded-lg hover:bg-white/5">
            <ChevronRight size={18} />
          </button>
          {isToday ? (
            <span className="ml-1 px-2 py-0.5 text-[11px] bg-violet-500/20 text-violet-400 rounded-full font-medium">오늘</span>
          ) : (
            <button onClick={goToday}
              className="ml-1 px-2.5 py-1 text-xs bg-white/5 text-white/40 rounded-lg hover:bg-white/10 hover:text-white/60">
              오늘로
            </button>
          )}
        </div>
        <div className="absolute right-4 flex items-center gap-2">
          <div className="flex -space-x-1.5">
            {peers.slice(0, 5).map((p, i) => (
              <div key={i} className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] font-bold text-white border-2 overflow-hidden ${p.isMe ? 'border-violet-400' : 'border-[#0d0b14]'}`}
                style={{ backgroundColor: p.profileImage ? 'transparent' : p.color }} title={p.isMe ? `${p.name} (나)` : p.name}>
                {p.profileImage
                  ? <img src={p.profileImage} alt="" className="w-full h-full object-cover" />
                  : p.name?.charAt(0)}
              </div>
            ))}
          </div>
          {!connected && <WifiOff size={12} className="text-red-400" title="오프라인" />}
          <button onClick={() => setShowSearch(!showSearch)}
            className={`p-1 rounded ${showSearch ? 'bg-violet-400/20 text-violet-400' : 'text-white/30 hover:text-white hover:bg-white/5'}`}>
            <Search size={14} />
          </button>
        </div>
      </div>

      {/* 에디터 */}
      <Toolbar editor={editor} onImageUpload={handleImageButton} />
      <EditorContent editor={editor} />
      <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileSelect} />

      {/* 검색 패널 */}
      {showSearch && (
        <div className="border-t border-white/5">
          <div className="px-3 py-2 flex gap-2">
            <div className="flex-1 flex items-center bg-white/[0.03] border border-white/[0.06] rounded-lg px-2.5">
              <Search size={12} className="text-white/30 flex-shrink-0" />
              <input value={searchInput} onChange={e => setSearchInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchSubmit()}
                placeholder="업무 내용 검색..."
                className="flex-1 bg-transparent text-white text-xs py-1.5 px-2 focus:outline-none placeholder-white/30" />
              {searchInput && <button onClick={clearSearch} className="text-white/30 hover:text-white"><X size={12} /></button>}
            </div>
            <button onClick={handleSearchSubmit}
              className="px-3 py-1.5 bg-violet-500/20 text-violet-400 text-xs rounded-lg hover:bg-violet-500/30">검색</button>
          </div>
          {searchQuery && (
            <div className="px-3 pb-3 max-h-48 overflow-y-auto">
              {searching ? (
                <p className="text-white/30 text-xs text-center py-3">검색 중...</p>
              ) : searchResults.length === 0 ? (
                <p className="text-white/30 text-xs text-center py-3">결과 없음</p>
              ) : (
                <div className="space-y-1">
                  <p className="text-white/30 text-[11px] mb-1">{searchResults.length}건</p>
                  {searchResults.map(r => (
                    <button key={r.id} onClick={() => { setDate(r.workDate); clearSearch(); }}
                      className={`w-full text-left px-2.5 py-2 rounded-lg text-xs transition-colors ${
                        r.workDate === date ? 'bg-violet-500/20 border border-violet-400/30' : 'bg-white/[0.02] hover:bg-white/[0.05] border border-transparent'
                      }`}>
                      <span className="text-white font-medium">{r.workDate.slice(5).replace('-', '.')}</span>
                      <span className="text-white/30 ml-1">{formatWeekday(r.workDate).replace('요일', '')}</span>
                      <p className="text-white/40 truncate mt-0.5">{getSnippet(r.content, searchQuery)}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── 메인 페이지 ───
export default function PaperAdminBoardsPage() {
  const [boards, setBoards] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', sortOrder: 0 });
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const isSuperAdmin = me?.superAdminYn === 'Y';

  const load = () => axios.get('/api/paper-task/boards').then(r => setBoards(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm({ name: '', description: '', sortOrder: 0 }); setShowForm(true); };
  const openEdit = (b) => { setEditing(b); setForm({ name: b.name, description: b.description || '', sortOrder: b.sortOrder }); setShowForm(true); };
  const save = async () => {
    if (!form.name.trim()) return;
    if (editing) await axios.put(`/api/paper-task/boards/${editing.id}`, form);
    else await axios.post('/api/paper-task/boards', form);
    setShowForm(false);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await axios.delete(`/api/paper-task/boards/${id}`);
    load();
  };

  const moveBoard = async (index, dir) => {
    const newArr = [...boards];
    const target = index + dir;
    if (target < 0 || target >= newArr.length) return;
    [newArr[index], newArr[target]] = [newArr[target], newArr[index]];
    const orders = newArr.map((b, i) => ({ id: b.id, sortOrder: i }));
    setBoards(newArr);
    await axios.patch('/api/paper-task/boards/reorder', { orders });
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* 일일업무 */}
      <DailyWorkSection me={me} />

      {/* 게시판 목록 */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-bold text-white">게시판</h2>
        {isSuperAdmin && (
          <button onClick={openCreate} className="flex items-center gap-1 bg-violet-500 text-white text-xs px-3 py-1.5 rounded-lg hover:bg-violet-400 transition-colors">
            <Plus size={14} /> 추가
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {boards.map((b, idx) => (
          <div key={b.id} onClick={() => navigate(`${PAPER_BASE}/admin/tasks/${b.id}`)}
            className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-5 cursor-pointer hover:border-violet-400/30 transition-colors group">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <ClipboardList size={18} className="text-violet-400" />
                <h3 className="text-white font-semibold">{b.name}</h3>
              </div>
              {isSuperAdmin && (
                <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity" onClick={e => e.stopPropagation()}>
                  <button onClick={() => moveBoard(idx, -1)} disabled={idx === 0}
                    className="p-1 text-white/30 hover:text-white disabled:opacity-20"><ChevronUp size={14} /></button>
                  <button onClick={() => moveBoard(idx, 1)} disabled={idx === boards.length - 1}
                    className="p-1 text-white/30 hover:text-white disabled:opacity-20"><ChevronDown size={14} /></button>
                  <button onClick={() => openEdit(b)} className="p-1 text-white/30 hover:text-white"><Pencil size={14} /></button>
                  <button onClick={() => remove(b.id)} className="p-1 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
            {b.description && <p className="text-white/40 text-sm mb-3">{b.description}</p>}
            <div className="flex items-center gap-2 text-xs">
              {b.pendingCount > 0 ? (
                <span className="bg-violet-400/15 text-violet-400 px-2 py-0.5 rounded-full font-medium">{b.pendingCount}개 진행 중</span>
              ) : (
                <span className="bg-white/5 text-white/30 px-2 py-0.5 rounded-full">할일 없음</span>
              )}
            </div>
          </div>
        ))}
        {boards.length === 0 && (
          <p className="text-white/30 text-sm col-span-3 text-center py-12">게시판이 없습니다.</p>
        )}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-[#1a1725] border border-white/10 rounded-xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h2 className="text-white font-bold mb-4">{editing ? '게시판 수정' : '게시판 추가'}</h2>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              placeholder="게시판 이름" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-3 focus:outline-none focus:border-violet-400" />
            <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder="설명 (선택)" className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm mb-4 focus:outline-none focus:border-violet-400" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-white/40 text-sm hover:text-white">취소</button>
              <button onClick={save} className="px-4 py-2 bg-violet-500 text-white text-sm rounded-lg hover:bg-violet-400">저장</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
